// @flow
import { combineReducers } from 'redux';

import * as types from '../types/users';
import * as closeableTypes from '../types/closeable';
import * as common from './common';
import type { MAYBE_ERROR_TYPE, ERROR_TYPE } from '../types/common';
import type {
  USER_ACTION_TYPE,
  USER_TYPE,
} from '../types/users';

export type UsersState = {
  byId: { [number]: USER_TYPE },
  order: Array<number>,
  isFetching: boolean,
  nextPage: number,
  uploadingUsers: { [number]: USER_TYPE },
  orderUploadingUsers: Array<number>,
  isUploading: boolean,
  errors: { [number]: ERROR_TYPE },
  error: MAYBE_ERROR_TYPE,
  createUsersStep: number,
  selected: number,
  filters: Object,
  isCreating: boolean,
  creatingError: MAYBE_ERROR_TYPE
};

const byId = common.byId({
  fetched: [
    types.USERS_FETCH_SUCCEED,
  ],
  added: [
    types.ADD_USER_SUCCEED,
    types.USER_FETCH_SUCCEED,
  ],
  removed: [types.REMOVE_USER_SUCCEED],
  updated: [types.UPDATE_USER_SUCCEDED],
});

const order = common.order({
  fetched: [
    types.USERS_FETCH_SUCCEED,
  ],
  added: [
    types.ADD_USER_SUCCEED,
  ],
  removed: [types.REMOVE_USER_SUCCEED],
  cleared: [types.USER_FILTERS_CHANGED],
  updated: [types.UPDATE_USER_SUCCEDED],
});

const isFetching = common.isFetching({
  started: [types.USERS_FETCH_STARTED],
  succeed: [types.USERS_FETCH_SUCCEED],
  failed: [types.USERS_FETCH_FAILED],
});

const nextPage = (
  state: ?number = 1,
  action: USER_ACTION_TYPE,
): ?number => {
  switch (action.type) {
    case types.USERS_FETCH_SUCCEED: {
      const { next } = action.payload;
      return next != null ? Math.max(state || -1, next) : null;
    }
    case types.USERS_FETCH_FAILED: {
      return null;
    }
    default: {
      return state;
    }
  }
};

const error = common.error({
  populate: [
    types.USERS_FETCH_FAILED,
    types.USER_REMOVED_FAILED,
  ],
  clear: [
    types.USERS_FETCH_STARTED,
    types.USERS_FETCH_SUCCEED,
  ],
});

const creatingError = common.error({
  populate: [
    types.ADD_USER_FAILED,
  ],
  clear: [
    types.ADD_USER_STARTED,
    types.ADD_USER_CANCELED,
    types.ADD_USER_ERROR_CLEARED,
  ],
});

const filters = (
  state: Object = {},
  action: USER_ACTION_TYPE,
): Object => {
  switch (action.type) {
    case types.USER_FILTERS_CHANGED: {
      const { payload } = action;
      return {
        ...state,
        [payload.key]: payload.filter,
      };
    }
    default: {
      return state;
    }
  }
};

const uploadingUsers = common.byId({
  fetched: [types.UPLOADING_USERS_ADDED],
});

const orderUploadingUsers = common.order({
  fetched: [types.UPLOADING_USERS_ADDED],
  replaced: [
    types.UPLOADING_USERS_CLEARED,
    types.UPLOAD_USERS_SUCCEED,
  ],
});

const errors = common.errors({
  populate: [types.USER_FETCH_FAILED],
  clear: [types.USER_FETCH_SUCCEED],
});

const selected = common.mux({
  selected: [types.USER_SELECTED],
  allDeselected: [types.USER_SELECTION_CLEARED],
  default: -1,
});

const createUsersStep = common.mux({
  selected: [types.CREATE_USERS_STEP_UPDATED],
  allDeselected: [closeableTypes.CLOSEABLE_OPENED],
  default: 1,
});

const isUploading = common.isFetching({
  started: [types.UPLOAD_USERS_STARTED],
  succeed: [types.UPLOAD_USERS_SUCCEED],
});

const isCreating = common.isFetching({
  started: [types.ADD_USER_STARTED],
  succeed: [types.ADD_USER_SUCCEED],
  failed: [types.ADD_USER_FAILED],
});


const users = combineReducers({
  byId,
  order,
  isFetching,
  nextPage,
  error,
  errors,
  selected,
  filters,
  createUsersStep,
  uploadingUsers,
  isUploading,
  orderUploadingUsers,
  isCreating,
  creatingError,
});

export default users;


export const getOneUser = (state: UsersState, id: number): Object => state.byId[id];
export const getUserSelected = (state: UsersState) => getOneUser(state, state.selected);
export const getUserOrder = (state: UsersState): Array<number> => state.order;
export const getUsers = (
  state: UsersState,
): Array<Object> => state.order.map(id => getOneUser(state, id));
export const isUsersFetching = (state: UsersState): boolean => state.isFetching;
export const getUserNextPage = (state: UsersState): number => state.nextPage;
export const getUsersFetchError = (state: UsersState): MAYBE_ERROR_TYPE => state.error;
export const getUsersError = (state: UsersState): MAYBE_ERROR_TYPE => state.error;
export const getUserError = (state: UsersState, id: number): ERROR_TYPE => state.errors[id];
export const hasMoreUsers = (state: UsersState): boolean => (state.nextPage || typeof state.nextPage === 'string') && !state.isFetching;
export const getUserFilters = (state: UsersState): Object => state.filters;
export const getCurrentCreateUsersStep = (state: UsersState): number => state.createUsersStep;
export const isCreatingUser = (state: UsersState): boolean => state.isCreating;
export const getErrorUserCreating = (state: UsersState): MAYBE_ERROR_TYPE => state.creatingError;
export const isUploadingUsers = (state: UsersState): boolean => state.isUploading;
export const getOneUploadingUser = (
  state: UsersState,
  id: number,
): Object => state.uploadingUsers[id];
export const getUploadingUsers = (
  state: UsersState,
): Array<Object> => state.orderUploadingUsers.map(id => getOneUploadingUser(state, id));

// TODO: hablar con mario
// export const getUploadingErrors = (state: UsersState): number => {
//   let count = 0;
//   let element;
//   state.orderUploadingUsers.map((id) => {
//     element = getOneUploadingUser(state, id);
//     !element.complete && count++;
//   });
//   return count;
// };

export const getUploadingErrors = (
  state: UsersState,
): number => state.orderUploadingUsers.map(
  id => getOneUploadingUser(state, id),
).filter(user => !user.completed).length;



// WEBPACK FOOTER //
// ./src/scripts/reducers/users.js